import { AfterViewInit, Component, ElementRef, Input, ViewChild, OnInit, ChangeDetectorRef } from '@angular/core';
import { setting } from '../../../../../../setting';

@Component({
  selector: 'usb-photos',
  templateUrl: './photos.component.html',
  styleUrls: ['./photos.component.scss']
})
export class PhotosComponent implements OnInit, AfterViewInit {
  @Input() images: any[] = [];
  @Input() videos: any[] | null = null;
  @ViewChild('carouselTrack') carouselTrack!: ElementRef;

  duplicatedImages: any[] = [];
  currentIndex = 0;
  loading = false;
  public getClientsSettingAppBooking: any;
  private isLooping = false;
  private observer: IntersectionObserver | null = null;

  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * Lista unificada de slides: concatena imágenes + videos (normalizados) para que
   * ambos se muestren en el carrusel. Antes priorizaba videos sobre imágenes y descartaba
   * la galería de fotos cuando el modal genérico (information) pasaba ambos inputs
   * (caso típico de la categoría ticket).
   */
  get slides(): any[] {
    const imgs = (this.images || []).map((i: any) => ({ ...i, _kind: 'image' as const }));
    const vids = (this.videos || [])
      .map((v: any) => this.normalizeVideo(v))
      .filter((v: any) => !!v);
    return [...imgs, ...vids];
  }

  /**
   * Acepta videos con cualquier shape ({ src } | { url } | { thumbnail } | string) y los
   * deja en el shape que esperan isVideo() y getItemSrc(): { type: 'video', image, src }.
   */
  private normalizeVideo(v: any): any {
    if (!v) return null;
    const src: string =
      typeof v === 'string' ? v
      : v.src ?? v.url ?? v.thumbnail ?? v.video ?? '';
    if (!src) return null;
    return { type: 'video', image: src, src, description: v.description ?? '' };
  }

  get hasMixedMedia(): boolean {
    const s = this.slides;
    if (!s || s.length < 2) return false;
    return s.some(i => this.isVideo(i)) && s.some(i => !this.isVideo(i));
  }

  isVideo(item: any): boolean {
    if (!item) return false;
    if (item._kind === 'video') return true;
    return (item.type || '').toLowerCase() === 'video';
  }

  isCurrentVideo(): boolean {
    const current = this.slides[this.currentIndex];
    return this.isVideo(current);
  }

  getItemSrc(item: any): string {
    return item?.image ?? item?.thumbnail ?? item?.url ?? '';
  }

  ngOnInit() {
    this.getClientsSettingAppBooking = JSON.parse(localStorage.getItem(setting.name));
    if (this.slides.length > 0) {
      this.duplicatedImages = [...this.slides, ...this.slides, ...this.slides];
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit() {
    if (this.slides.length > 0) {
      this.isLooping = true;
      this.centerToRealImage(0);
      this.updateActiveIndex();
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.centerToRealImage(this.currentIndex);
        this.updateActiveIndex();
        this.setupIntersectionObserver();
        setTimeout(() => { this.isLooping = false; }, 300);
      }));
    }
  }

  setupIntersectionObserver() {
    if (!this.carouselTrack || !this.carouselTrack.nativeElement) return;
    const track = this.carouselTrack.nativeElement;

    // Solo observamos las slides del bloque del medio (índices slides.length .. 2*slides.length-1).
    // Las del primer y tercer bloque son "fantasma" para el loop visual; no queremos que el
    // observer nos teletransporte a ellas.
    const middleStart = this.slides.length;
    const middleEnd = 2 * this.slides.length;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLooping) {
          const slide = entry.target as HTMLElement;
          const slideIndex = parseInt(slide.dataset['index'] || '0');
          const realIndex = slideIndex % this.slides.length;

          // Si la slide "intrusa" del primer bloque se intersecta, teletransportamos
          // al equivalente del bloque del medio.
          if (slideIndex < middleStart) {
            this.isLooping = true;
            const targetIndex = slideIndex + this.slides.length;
            track.scrollLeft = this.computeScrollLeftFor(targetIndex);
            this.currentIndex = realIndex;
            this.cdr.detectChanges();
            setTimeout(() => { this.isLooping = false; }, 400);
          } else if (slideIndex >= middleEnd) {
            this.isLooping = true;
            const targetIndex = slideIndex - this.slides.length;
            track.scrollLeft = this.computeScrollLeftFor(targetIndex);
            this.currentIndex = realIndex;
            this.cdr.detectChanges();
            setTimeout(() => { this.isLooping = false; }, 400);
          } else {
            // Slide del bloque del medio: solo actualizamos currentIndex.
            if (realIndex !== this.currentIndex) {
              this.currentIndex = realIndex;
              this.cdr.detectChanges();
            }
          }
        }
      });
    }, { threshold: 0.6, root: track });

    setTimeout(() => {
      const slides = track.querySelectorAll('.custom-carousel-slide');
      slides.forEach((slide, idx) => {
        // Solo observamos las slides del bloque del medio.
        if (idx >= middleStart && idx < middleEnd) {
          this.observer?.observe(slide);
        }
      });
    }, 100);
  }

  /**
   * Calcula el scrollLeft exacto para que la slide `index` quede centrada en el track.
   * Fórmula: offsetLeft_slide - (clientWidth/2 - slideWidth/2)
   * Esto neutraliza el scroll-snap porque escribimos directo en scrollLeft.
   */
  private computeScrollLeftFor(index: number): number {
    const track = this.carouselTrack.nativeElement;
    const slide = track.children[index] as HTMLElement;
    if (!slide) return track.scrollLeft;
    return slide.offsetLeft - (track.clientWidth / 2) + (slide.offsetWidth / 2);
  }

  visibleDots: any[] = [];
  private lastIndex = -1;
  private lastTotal = 0;

  trackByDot(index: number, dot: any): string {
    return dot.isEllipsis ? `ellipsis_${index}` : `dot_${dot.index}`;
  }

  updateVisibleDots(): void {
    const total = this.slides.length;
    const current = this.currentIndex;

    if (this.lastTotal === total && this.lastIndex === current && this.visibleDots.length > 0) {
      return;
    }

    this.lastTotal = total;
    this.lastIndex = current;

    const dots: any[] = [];

    if (total <= 3) {
      for (let i = 0; i < total; i++) {
        dots.push({ index: i, label: (i + 1).toString(), isEllipsis: false, id: `dot_${i}` });
      }
      this.visibleDots = dots;
      return;
    }

    let startVisible: number;
    if (current <= 1) {
      startVisible = 0;
    } else if (current >= total - 2) {
      startVisible = total - 3;
    } else {
      startVisible = current - 1;
    }

    for (let i = 0; i < 3; i++) {
      const index = startVisible + i;
      if (index < total) {
        dots.push({ index: index, label: (index + 1).toString(), isEllipsis: false, id: `dot_${index}` });
      }
    }

    const lastIndex = total - 1;
    const lastVisible = dots[dots.length - 1]?.index;

    if (lastVisible < lastIndex) {
      dots.push({ index: -1, label: '...', isEllipsis: true, id: 'ellipsis_1' });
      dots.push({ index: lastIndex, label: (lastIndex + 1).toString(), isEllipsis: false, id: `dot_${lastIndex}` });
    }

    this.visibleDots = dots;
  }

  getVisibleDots(): any[] {
    this.updateVisibleDots();
    return this.visibleDots;
  }

  setCurrentIndex(index: number): void {
    if (this.currentIndex !== index) {
      this.currentIndex = index;
      this.updateVisibleDots();
    }
  }

  prevImage(): void {
    if (this.loading) return;
    this.loading = true;

    let newIndex = this.currentIndex - 1;
    if (newIndex < 0) {
      newIndex = this.slides.length - 1;
    }

    this.selectImage(newIndex);
    setTimeout(() => { this.loading = false; }, 500);
  }

  nextImage(): void {
    if (this.loading) return;
    this.loading = true;

    let newIndex = this.currentIndex + 1;
    if (newIndex >= this.slides.length) {
      newIndex = 0;
    }

    this.selectImage(newIndex);
    setTimeout(() => { this.loading = false; }, 500);
  }

  centerToRealImage(index: number) {
    if (this.carouselTrack && this.carouselTrack.nativeElement && this.duplicatedImages.length > 0) {
      const realIndex = index % this.slides.length;
      const targetIndex = realIndex + this.slides.length;
      this.carouselTrack.nativeElement.scrollLeft = this.computeScrollLeftFor(targetIndex);
      this.currentIndex = realIndex;
      this.cdr.detectChanges();
    }
  }

  updateActiveIndex() {
    if (this.carouselTrack && this.carouselTrack.nativeElement && this.carouselTrack.nativeElement.children && !this.isLooping) {
      const track = this.carouselTrack.nativeElement;
      const slides = track.children;
      const trackCenter = track.scrollLeft + (track.clientWidth / 2);

      let closestIndex = 0;
      let closestDistance = Infinity;

      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i] as HTMLElement;
        const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
        const distance = Math.abs(trackCenter - slideCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      }

      const newIndex = closestIndex % this.slides.length;
      if (newIndex !== this.currentIndex) {
        this.currentIndex = newIndex;
        this.cdr.detectChanges();
      }
    }
  }

  selectImage(index: number) {
    if (this.carouselTrack && this.carouselTrack.nativeElement && this.duplicatedImages.length > 0) {
      const targetIndex = index + this.slides.length;
      this.isLooping = true;
      this.carouselTrack.nativeElement.scrollTo({
        left: this.computeScrollLeftFor(targetIndex),
        behavior: 'smooth'
      });
      this.currentIndex = index;
      this.cdr.detectChanges();
      setTimeout(() => { this.isLooping = false; }, 400);
    }
  }

  onScroll() {
    this.updateActiveIndex();
  }

  /**
   * Salta al siguiente slide del tipo solicitado (image | video), con loop.
   * Si no hay ninguno, no hace nada.
   */
  goToNextOfType(type: 'image' | 'video'): void {
    const slides = this.slides;
    if (!slides || slides.length === 0) return;
    const isMatch = (item: any) => type === 'video' ? this.isVideo(item) : !this.isVideo(item);
    let newIndex = -1;
    for (let step = 1; step <= slides.length; step++) {
      const candidate = (this.currentIndex + step) % slides.length;
      if (isMatch(slides[candidate])) {
        newIndex = candidate;
        break;
      }
    }
    if (newIndex !== -1) this.selectImage(newIndex);
  }

  goToPrevOfType(type: 'image' | 'video'): void {
    const slides = this.slides;
    if (!slides || slides.length === 0) return;
    const isMatch = (item: any) => type === 'video' ? this.isVideo(item) : !this.isVideo(item);
    let newIndex = -1;
    for (let step = 1; step <= slides.length; step++) {
      const candidate = (this.currentIndex - step + slides.length) % slides.length;
      if (isMatch(slides[candidate])) {
        newIndex = candidate;
        break;
      }
    }
    if (newIndex !== -1) this.selectImage(newIndex);
  }
}
